import React, { useState, useEffect } from 'react';
import useSupportStore from '../../../store/useSupportStore';
import useAuthStore from '../../../store/useAuthStore';
import useProfileStore from '../../../store/builders/useProfileStore';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

// Helper to format date and time
const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
        return new Date(dateString).toLocaleString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true
        });
    } catch (e) {
        return "Invalid Date";
    }
};

// Status Badge Component
const StatusBadge = ({ status }) => {
    const statusClasses = {
        Open: 'bg-blue-100 text-blue-800',
        'In Progress': 'bg-yellow-100 text-yellow-800',
        Resolved: 'bg-green-100 text-green-800',
        Closed: 'bg-gray-100 text-gray-800',
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
            {status}
        </span>
    );
};

const SupportDeck = () => {
    const { createTicket, fetchMyTickets, tickets, isLoading, isSubmitting } = useSupportStore();
    const { user } = useAuthStore();
    const { profile, fetchProfile } = useProfileStore();

    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        district: '',
        taluk: '',
        query: ''
    });

    useEffect(() => {
        fetchMyTickets();
        if (!profile) {
            fetchProfile();
        }
    }, [fetchMyTickets, fetchProfile, profile]);

    useEffect(() => {
        if (profile?.builder) {
            setForm(prev => ({
                ...prev,
                name: profile.builder.engineer_name || user?.name || '',
                email: profile.builder.email || user?.email || '',
                phone: profile.builder.contact_number || '',
                district: profile.builder.district || '',
                taluk: profile.builder.taluk || '',
            }));
        }
    }, [profile, user]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.query.trim()) {
            toast.error("Please describe your query.");
            return;
        }

        const result = await createTicket({ ...form });
        if (result.success) {
            setForm(prev => ({ ...prev, query: '' })); // Clear only the query field
        }
    };

    return (
        <div className="p-2 sm:p-4 md:p-6 space-y-6 font-sans w-full max-w-5xl mx-auto">
            {/* Ticket Form */}
            <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 shadow-sm w-full">
                <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Create a Support Ticket</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="block text-xs font-medium text-gray-500">Name</label>
                            <input type="text" value={form.name} className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-sm text-gray-700 cursor-not-allowed" readOnly />
                        </div>
                        <div className="space-y-1">
                            <label className="block text-xs font-medium text-gray-500">Email</label>
                            <input type="email" value={form.email} className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-sm text-gray-700 cursor-not-allowed" readOnly />
                        </div>
                        <div className="space-y-1">
                            <label className="block text-xs font-medium text-gray-500">Phone</label>
                            <input type="text" value={form.phone} className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-sm text-gray-700 cursor-not-allowed" readOnly />
                        </div>
                        <div className="space-y-1">
                            <label className="block text-xs font-medium text-gray-500">District</label>
                            <input type="text" value={form.district} className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-sm text-gray-700 cursor-not-allowed" readOnly />
                        </div>
                        <div className="space-y-1 sm:col-span-2">
                            <label className="block text-xs font-medium text-gray-500">Taluk</label>
                            <input type="text" value={form.taluk} className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-sm text-gray-700 cursor-not-allowed" readOnly />
                        </div>
                    </div>
                    
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">Your Query <span className="text-red-500">*</span></label>
                        <textarea
                            name="query"
                            value={form.query}
                            onChange={(e) => setForm(prev => ({...prev, query: e.target.value}))}
                            placeholder="Please describe your issue in detail..."
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm resize-none"
                        />
                    </div>
                    
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 sm:px-5 py-2 sm:py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center justify-center min-w-[100px] sm:min-w-[120px] disabled:opacity-60"
                        >
                            {isSubmitting ? <Loader2 className="animate-spin" /> : 'Submit Ticket'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Support History */}
            <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 shadow-sm w-full">
                <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Your Support History</h2>
                {/* Responsive Table Wrapper */}
                <div className="w-full overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="text-left py-2 sm:py-3 px-2 sm:px-3 font-medium text-gray-600 whitespace-nowrap">Created</th>
                                <th className="text-left py-2 sm:py-3 px-2 sm:px-3 font-medium text-gray-600">Query</th>
                                <th className="text-center py-2 sm:py-3 px-2 sm:px-3 font-medium text-gray-600">Status</th>
                                <th className="text-center py-2 sm:py-3 px-2 sm:px-3 font-medium text-gray-600">Priority</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="4" className="text-center py-8 text-gray-500">Loading tickets...</td>
                                </tr>
                            ) : tickets.length > 0 ? (
                                tickets.map((ticket) => (
                                    <React.Fragment key={ticket.id}>
                                        <tr className="hover:bg-gray-50">
                                            <td className="py-2 sm:py-3 px-2 sm:px-3 text-gray-600 whitespace-nowrap">{formatDateTime(ticket.created_at)}</td>
                                            <td className="py-2 sm:py-3 px-2 sm:px-3 text-gray-700 min-w-[150px] sm:min-w-[250px] max-w-xs sm:max-w-sm truncate" title={ticket.query}>{ticket.query}</td>
                                            <td className="py-2 sm:py-3 px-2 sm:px-3 text-center"><StatusBadge status={ticket.status} /></td>
                                            <td className="py-2 sm:py-3 px-2 sm:px-3 text-center text-gray-600 font-medium">{ticket.priority}</td>
                                        </tr>
                                        {ticket.resolution_notes && (
                                            <tr>
                                                <td colSpan="4" className="bg-blue-50 px-4 py-3 rounded-md">
                                                    <span className="text-sm font-medium text-blue-700 mb-1">Admin Note:</span>
                                                    <div className="text-sm text-gray-600 whitespace-pre-wrap">{ticket.resolution_notes}</div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center py-8 text-gray-500">You have no support tickets yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SupportDeck;